/*
  Stoffeya: shows shoppers what is already in their basket.

  <stoffeya-cart-state data-kind="card" data-product-id>
    Inside a product card's quick add. The bag icon becomes a filled bag with a
    check once the product is in the cart. When Shopify refuses an add (the
    whole stock is already in the cart), a short note appears instead of the
    theme's grey button, which stayed locked until the page was reloaded.
  <stoffeya-cart-state data-kind="product" data-product-id>
    Under the product page buttons: "Im Warenkorb: 10 m".

  The cart comes from Liquid on page load (#StoffeyaCartState in
  layout/theme.liquid, no extra request) and from /cart.js after every change.
  The header cart icon is re-rendered after each add, change and removal,
  including Meterware's own add path that publishes no cartUpdate event, so
  that icon is what gets watched.
*/
if (!customElements.get('stoffeya-cart-state')) {
  const CHANGE_EVENT = 'stoffeya:cart-state';
  const NOTE_MS = 4000;
  const source = document.getElementById('StoffeyaCartState');
  const texts = source?.dataset || {};
  const numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'de', { maximumFractionDigits: 2 });

  // Metres in one unit of a cart line, or null when it is not sold by length
  const metresPerUnit = (measurement) => {
    const value = parseFloat(measurement?.quantity_value);
    if (!(value > 0)) return null;
    if (measurement.quantity_unit === 'm') return value;
    if (measurement.quantity_unit === 'cm') return value / 100;
    return null;
  };

  // Product id -> { quantity, metres, byLength }; a product can sit in the cart
  // as several lines (Meterware: one line per length). Without a unit price
  // in Shopify the length is unknown, so those show "3×" rather than "3 m".
  const summarize = (items) => {
    const products = new Map();
    items.forEach((item) => {
      const id = String(item.product_id);
      const entry = products.get(id) || { quantity: 0, metres: 0, byLength: true };
      entry.quantity += item.quantity;
      const perUnit = metresPerUnit(item.unit_price_measurement);
      if (perUnit === null) entry.byLength = false;
      else entry.metres += item.quantity * perUnit;
      products.set(id, entry);
    });
    return products;
  };

  const readInitial = () => {
    try {
      return JSON.parse(source?.textContent || '[]');
    } catch (error) {
      return [];
    }
  };

  let products = summarize(readInitial());

  const amountText = (entry) =>
    entry.byLength
      ? (texts.metres || '[count] m').replace('[count]', numberFormat.format(entry.metres))
      : (texts.count || '[count]×').replace('[count]', entry.quantity);

  let refreshTimer;
  let latestRefresh = 0;
  const refresh = () => {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(async () => {
      const run = ++latestRefresh;
      try {
        const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const cart = await response.json();
        // A later change is already being read
        if (run !== latestRefresh) return;
        products = summarize(cart.items || []);
        document.dispatchEvent(new CustomEvent(CHANGE_EVENT));
      } catch (error) {
        // Offline for a moment: keep the last known state
      }
    }, 60);
  };

  const cartIcon = document.getElementById('cart-icon-bubble');
  if (cartIcon) new MutationObserver(refresh).observe(cartIcon, { childList: true, subtree: true });

  // Back and forward show the page as it was before the add: from memory
  // (bfcache) or from the browser's copy of the HTML with the old Liquid state
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refresh();
  });
  if (performance.getEntriesByType?.('navigation')[0]?.type === 'back_forward') refresh();

  // The card whose quick add was submitted last; its refusal gets the note
  let lastCardSubmit = null;
  document.addEventListener(
    'submit',
    (event) => {
      lastCardSubmit = event.target.closest?.('.quick-add--compact') || null;
    },
    true
  );

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartError, (event) => {
      const host = lastCardSubmit;
      if (!host || event?.source !== 'product-form') return;
      const variantId = host.querySelector('form [name="id"]')?.value;
      if (String(variantId) !== String(event.productVariantId)) return;
      // product-form.js locks the button right after publishing this event
      setTimeout(() => host.querySelector('stoffeya-cart-state')?.refused(event), 0);
    });
  }

  class CartState extends HTMLElement {
    connectedCallback() {
      this.host = this.dataset.kind === 'card' ? this.closest('.quick-add') : null;
      this.button = this.host?.querySelector('.quick-add__submit');
      document.addEventListener(CHANGE_EVENT, this.render);
      this.addEventListener('click', this.onClick);
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener(CHANGE_EVENT, this.render);
      this.removeEventListener('click', this.onClick);
      clearTimeout(this.noteTimer);
    }

    get entry() {
      return products.get(this.dataset.productId);
    }

    render = () => {
      const entry = this.entry;
      if (this.host) {
        this.host.classList.toggle('is-in-cart', Boolean(entry));
        const label = this.querySelector('.cart-state__label');
        if (label && this.button) {
          if (entry) this.button.setAttribute('aria-describedby', label.id);
          else this.button.removeAttribute('aria-describedby');
        }
        return;
      }
      const line = this.querySelector('.cart-state__line');
      if (!line) return;
      line.hidden = !entry;
      if (entry) {
        this.querySelector('.cart-state__text').textContent = (texts.inCart || '[amount]').replace(
          '[amount]',
          amountText(entry)
        );
      }
    };

    // "Ansehen" opens the cart drawer where there is one
    onClick = (event) => {
      if (!event.target.closest('.cart-state__link')) return;
      const drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;
      event.preventDefault();
      const modal = this.closest('quick-add-modal');
      if (modal) modal.hide(true);
      drawer.open(event.target);
    };

    refused(event) {
      const form = this.host?.querySelector('product-form');
      if (form) form.error = false;
      if (this.button) {
        this.button.removeAttribute('aria-disabled');
        this.button.querySelector('.sold-out-message')?.classList.add('hidden');
        this.button.querySelector('.quick-add__label')?.classList.remove('hidden');
      }
      const shopifyText = typeof event.errors === 'string' ? event.errors : '';
      const text = this.entry ? texts.maxInCart : shopifyText || texts.refused;
      const note = this.querySelector('.cart-state__note');
      if (!note || !text) return;
      note.textContent = text;
      note.hidden = false;
      clearTimeout(this.noteTimer);
      this.noteTimer = setTimeout(() => {
        note.hidden = true;
      }, NOTE_MS);
      // The cart may have changed in another tab
      refresh();
    }
  }

  customElements.define('stoffeya-cart-state', CartState);
}
